import React, { useState } from "react";
import "./PreviewWindow.scss";
import prev_top_1 from "./Preview1_top.png";
import prev_bottom_1 from "./Preview1_bottom.png";
import prev_top_2 from "./Preview2_top.png";
import prev_bottom_2 from "./Preview2_bottom.png";
import prev_top_3 from "./Preview3_top.png";
import prev_bottom_3 from "./Preview3_bottom.png";
import prev_top_4 from "./Preview4_top.png";
import prev_bottom_4 from "./Preview4_bottom.png";
import { Player } from "video-react";
import PreviewVideoPlayer from "./PreviewVideoPlayer";

// ─── Skeleton / filler UI ────────────────────────────────────────────────────

const Skeleton = ({ lines = 4 }) => (
  <div className="Skeleton">
    <div className="Skeleton__title" />
    {Array.from({ length: lines }).map((_, i) => (
      <div key={i} className={`Skeleton__line ${i % 3 === 2 ? "short" : ""}`} />
    ))}
  </div>
);

const MockNav = () => (
  <div className="MockNav">
    <div className="MockNav__logo" />
    <div className="MockNav__links">
      <div className="MockNav__link" />
      <div className="MockNav__link" />
      <div className="MockNav__link" />
    </div>
  </div>
);

// ─── Ad Block ────────────────────────────────────────────────────────────────

const AdBlock = ({ advertisement, image, className = "" }) => (
  <div className={`AdBlock ${className}`}>
    <div className="AdBlock__badge">Advertisement</div>
    {image || advertisement?.desktop_image?.link ? (
      <img src={image || advertisement?.desktop_image?.link} alt={advertisement.title || "Ad"} />
    ) : (
      <div className="AdBlock__inner">
        <div className="AdBlock__icon">◈</div>
        <div className="AdBlock__copy">
          <span className="AdBlock__title">{advertisement?.title || "Your Advertisement"}</span>
          <span className="AdBlock__sub">{advertisement?.description || "Compelling message goes here"}</span>
        </div>
        <button className="AdBlock__cta">{advertisement?.cta || "Learn More"}</button>
      </div>
    )}
  </div>
);

// ─── Mock Browser Shell ──────────────────────────────────────────────────────

const MockBrowser = ({ children }) => (
  <div className="MockBrowser">
    <div className="blur" />
    <div className="MockBrowser__chrome">
      <div className="MockBrowser__dots">
        <span /><span /><span />
      </div>
      <div className="MockBrowser__address">
        <span className="MockBrowser__lock">🔒</span>
        https://edgubilig.org
      </div>
    </div>
    <div className="MockBrowser__viewport">{children}</div>
  </div>
);

// ─── Placement Types ─────────────────────────────────────────────────────────

const PreviewType1 = ({ advertisement, image, video }) => {
  return <MockBrowser>
    <img alt={"top_1"} src={prev_top_1} />
    {
      advertisement?.mediaType === "video" ? <PreviewVideoPlayer playsInline src={URL.createObjectURL(video)} /> :
        <AdBlock image={image} advertisement={advertisement} className="AdBlock--leaderboard" />
    }

    <img alt={"bottom_1"} src={prev_bottom_1} />
  </MockBrowser>;
};

const PreviewType2 = ({ advertisement, image, video }) => (
  <MockBrowser>
    <img alt={"top_2"} src={prev_top_2} />
    {
      advertisement?.mediaType === "video" ? <PreviewVideoPlayer playsInline src={URL.createObjectURL(video)} /> :
        <AdBlock image={image} advertisement={advertisement} className="AdBlock--mrec" />
    }

    <img alt={"bottom_2"} src={prev_bottom_2} />
  </MockBrowser>
);

const PreviewType3 = ({ advertisement, image, video }) => (
  <MockBrowser>
    <img alt={"top_3"} src={prev_top_3} />
    {
      advertisement?.mediaType === "video" ? <PreviewVideoPlayer playsInline src={URL.createObjectURL(video)} /> :
        <AdBlock image={image} advertisement={advertisement} className="AdBlock--native" />
    }

    <img alt={"bottom_3"} src={prev_bottom_3} />
  </MockBrowser>
);

const PreviewType4 = ({ advertisement, image, video }) => (
  <MockBrowser>
    <img alt={"top_4"} src={prev_top_4} />
    {
      advertisement?.mediaType === "video" ? <PreviewVideoPlayer playsInline src={URL.createObjectURL(video)} /> :
        <AdBlock image={image} advertisement={advertisement} className="AdBlock--footer" />
    }

    <img alt={"bottom_4"} src={prev_bottom_4} />
  </MockBrowser>
);

// ─── Placement Config ────────────────────────────────────────────────────────

const PLACEMENTS = [
  {
    id: "Option1",
    label: "Leaderboard",
    icon: "▬",
    Component: PreviewType1,
  },
  {
    id: "Option2",
    label: "In-Content (beginning)",
    icon: "☰",
    Component: PreviewType2,
  },
  {
    id: "Option3",
    label: "In-Content (mid)",
    icon: "☰",
    Component: PreviewType3,
  },
  {
    id: "Option4",
    label: "Footer Banner",
    icon: "▬",
    Component: PreviewType4,
  },
];

// ─── Main Modal ──────────────────────────────────────────────────────────────

const PreviewWindow = ({ setPreview, advertisement, image, video }) => {
  const [activePlacement, setActivePlacement] = useState(advertisement?.placement[0].id);

  if (!advertisement) return null;

  const active = PLACEMENTS.find((p) => p.id === activePlacement);
  const ActiveComponent = active.Component;

  return (
    <div className="PreviewWindow">
      <div
        className="PreviewWindow__blur"
        onClick={() => setPreview(null)}
      />

      <div className="PreviewWindow__form">
        {/* Header */}
        <div className="PreviewWindow__header">
          <div className="PreviewWindow__header-left">
            <span className="PreviewWindow__eyebrow">Ad Preview</span>
            <h2 className="PreviewWindow__title">
              {advertisement.title || "Untitled Campaign"}
            </h2>
          </div>
          <button
            className="PreviewWindow__close"
            onClick={() => setPreview(null)}
            aria-label="Close preview"
          >
            ✕
          </button>
        </div>

        {/* Placement Tabs */}
        <div className="PreviewWindow__tabs" role="tablist">
          {PLACEMENTS.filter((el) => {
            let placementsId = advertisement.placement.map((el) => (el.id));
            return !!placementsId.includes(el.id);
          }).map((p) => (
            <button type={"button"}
                    key={p.id}
                    role="tab"
                    aria-selected={activePlacement === p.id}
                    className={`PlacementTab ${activePlacement === p.id ? "PlacementTab--active" : ""}`}
                    onClick={() => setActivePlacement(p.id)}
            >
              <span className="PlacementTab__icon">{p.icon}</span>
              <span className="PlacementTab__label">{p.label}</span>
              <span className="PlacementTab__desc">{p.desc}</span>
            </button>
          ))}
        </div>

        {/* Preview Stage */}
        <div className="PreviewWindow__stage">
          <ActiveComponent image={image && URL.createObjectURL(image)} video={video} advertisement={advertisement} />
        </div>

        {/* Footer */}
        <div className="PreviewWindow__footer">
          <span className="PreviewWindow__placement-name">
            Placement {activePlacement} of {PLACEMENTS.length} — <strong>{active.label}</strong>
          </span>
          <div className="PreviewWindow__footer-actions">
            <button type={"button"}
                    className="PreviewWindow__btn PreviewWindow__btn--ghost"
                    onClick={() => setPreview(null)}
            >
              Cancel
            </button>
            <button type={"submit"} className="PreviewWindow__btn PreviewWindow__btn--primary">
              Confirm Placement
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewWindow;
